import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material"
import { useEffect, useState } from "react"
import {
  selectEnteredInvoiceInformation,
  selectLGInternalUser,
  updateInvoiceDetail,
} from "../../activeScreenSlice"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import useShowError from "../../hooks/useShowError"
import { getSupplierDetails } from "../../service/SupplierDetails"

import {
  StyledErrorIcon,
  StyledIcon,
  StyledStatusIcon,
} from "../FormTextBox/FormTextbox.styles"

import {
  StyledHeader,
  StyledColumn,
  StyledRow,
  StyledLeftColumn,
  StyledRightColumn,
  StyledMenuItem,
  StyledSubHeader,
} from "./BankDetailsLookup.styles"

import { SupplierBankRead } from "../../api/generated"

export default function BankDetailsLookup() {
  const showError = useShowError()
  const dispatch = useAppDispatch()
  const LGInternalUser = useAppSelector(selectLGInternalUser);
  const invoiceInputs = useAppSelector(selectEnteredInvoiceInformation)

  const [bankDetailsOptions, setBankDetailsOptions] =
    useState<SupplierBankRead[]>()

  const [selectedBankDetailsId, setSelectedBankDetailsId] = useState<
    string | number | undefined
  >(invoiceInputs.supplierBankDetailsId)

  function handleUpdateStore(key: string, value?: any) {
    dispatch(updateInvoiceDetail({ key: key, value: value }))
  }

  useEffect(() => {
    if (
      invoiceInputs.supplierId !== undefined ||
      invoiceInputs.supplierSiteId !== undefined
    ) {
      // clear in case of site change
      handleUpdateStore("supplierBankDetailsId", undefined)

      getSupplierDetails(invoiceInputs.supplierId)
        .then((res) => {
          const bankDetails = res.supplier_bank_details.filter(
            (bankDetail) =>
              bankDetail.supplier_site_id === invoiceInputs.supplierSiteId,
          )
          setBankDetailsOptions(bankDetails)
          if (bankDetails.length === 1) {
            setSelectedBankDetailsId(bankDetails[0].id)
          }
        })
        .catch((error) => {
          showError(
            "An error occurred while getting bank details options: " +
              error.name +
              " " +
              error.message,
          )
        })
    }
  }, [showError, invoiceInputs.supplierId, invoiceInputs.supplierSiteId])

  useEffect(() => {
    if (selectedBankDetailsId && bankDetailsOptions) {
      const bankDetails = bankDetailsOptions.find(
        (bank) => bank.id === selectedBankDetailsId,
      )
      if (bankDetails) {
        handleUpdateStore("supplierBankDetailsId", bankDetails.id)
      }
    } else {
      handleUpdateStore("supplierBankDetailsId", undefined)
    }
  }, [selectedBankDetailsId, bankDetailsOptions])

  return (
    <>
      <Box sx={{ display: "flex", gap: "12px" }}>
        <Box sx={{ width: "235px", mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Bank Details</InputLabel>
            <Select
              value={selectedBankDetailsId || ""}
              label="Bank Details"
              onChange={(e, v) => {
                setSelectedBankDetailsId(e.target.value)
              }}
              disabled={LGInternalUser}
            >
              {bankDetailsOptions &&
                bankDetailsOptions.map((bank) => (
                  <MenuItem value={bank.id} key={bank.id}>
                    <StyledMenuItem>
                      <StyledHeader>
                        <StyledColumn>{bank.bank_name}</StyledColumn>
                      </StyledHeader>
                      <StyledRow>
                        <StyledLeftColumn>Branch</StyledLeftColumn>
                        <StyledRightColumn>
                          {bank.branch_name}
                        </StyledRightColumn>
                      </StyledRow>
                      <StyledRow>
                        <StyledLeftColumn>Country</StyledLeftColumn>
                        <StyledRightColumn>
                          {`${bank.bank_country} (${bank.supplier_bank_account_country})`}
                        </StyledRightColumn>
                      </StyledRow>
                      <StyledRow>
                        <StyledLeftColumn>Currency</StyledLeftColumn>
                        <StyledRightColumn>
                          {bank.bank_account_currency}
                        </StyledRightColumn>
                      </StyledRow>
                      <StyledSubHeader>
                        <StyledColumn>Account</StyledColumn>
                      </StyledSubHeader>
                      <StyledRow>
                        <StyledLeftColumn>Name</StyledLeftColumn>
                        <StyledRightColumn>
                          {bank.account_name}
                        </StyledRightColumn>
                      </StyledRow>
                      <StyledRow>
                        <StyledLeftColumn>Number</StyledLeftColumn>
                        <StyledRightColumn>
                          {bank.supplier_bank_account_number}
                        </StyledRightColumn>
                      </StyledRow>
                      <StyledRow>
                        <StyledLeftColumn>Sort Code</StyledLeftColumn>
                        <StyledRightColumn>
                          {bank.branch_number}
                        </StyledRightColumn>
                      </StyledRow>
                      <StyledRow>
                        <StyledLeftColumn>Swift</StyledLeftColumn>
                        <StyledRightColumn>{bank.bic_swift}</StyledRightColumn>
                      </StyledRow>

                      <StyledRow>
                        <StyledLeftColumn>IBAN</StyledLeftColumn>
                        <StyledRightColumn>
                          {bank.supplier_iban}
                        </StyledRightColumn>
                      </StyledRow>
                    </StyledMenuItem>
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
        <StyledIcon>
          {invoiceInputs.supplierBankDetailsId && <StyledStatusIcon />}
          {invoiceInputs.supplierBankDetailsId === undefined && (
            <StyledErrorIcon />
          )}
        </StyledIcon>
      </Box>
    </>
  )
}
